import { getUserByLinkToken } from 'app/crud/auth.crud';
import useToast from 'app/hooks/useToast';
import { sanitizeRedirect } from 'app/models/linkLogin';
import { canOpenBackofficeSession } from 'app/models/user';
import { PATH_LOGIN } from 'app/router/routesConfigAuth';
import { takeLinkToken } from 'app/services/linkLogin.service';
import { getRedirect } from 'app/services/routes.service';
import { auth, scopes, systemSettings } from 'app/store/index';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

export const SETTINGS_WAIT_MS = 5000;

async function fetchLinkUser(token) {
  try {
    const response = await getUserByLinkToken(token);
    return response.status === 200 ? response.data?.data : null;
  } catch (error) {
    // Solo el estado o el mensaje: el error de axios lleva el token en config.headers y Sentry guarda la consola.
    console.warn('Acceso por enlace rechazado', error?.response?.status ?? error?.message);
    return null;
  }
}

function useLinkLoginPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const { t } = useTranslation();
  const { openToast } = useToast();
  const settings = useSelector(systemSettings.selectors.getSetting);
  const currentUser = useSelector(auth.selectors.getUser);
  const [destination, setDestination] = useState(null);

  // Una sola vez: takeLinkToken vacía el token.
  useEffect(() => {
    signIn(takeLinkToken(), sanitizeRedirect(new URLSearchParams(search).get('redirect')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // canAccess depende de los settings, que en un navegador nuevo aún no han llegado. Con tope: si su
  // petición falla, el saga no avisa y la pantalla se quedaría cargando.
  useEffect(() => {
    if (!destination) return undefined;

    if (!isEmpty(settings)) {
      history.replace(destination);
      return undefined;
    }

    const timeout = setTimeout(() => history.replace(destination), SETTINGS_WAIT_MS);
    return () => clearTimeout(timeout);
  }, [destination, history, settings]);

  async function signIn(token, redirect) {
    const user = token ? await fetchLinkUser(token) : null;

    if (!canOpenBackofficeSession(user)) {
      openToast({ error: true, message: t('auth:link_login_error') });
      history.replace(PATH_LOGIN);
      return;
    }

    // Otro usuario: Logout vacía también lo que los demás ducks guardan por usuario.
    if (currentUser && currentUser.guid !== user.guid) {
      dispatch(auth.actions.logout());
    }
    dispatch(auth.actions.loginWithLinkToken({ token, user }));
    dispatch(scopes.actions.getScopes());
    setDestination(redirect || getRedirect(user));
  }
}

export default useLinkLoginPage;
